import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { CMSTrackOnClickProvider } from '@/cmsComponents/CMSTrackOnClickProvider'
import { useAmplitude } from '@/contexts/AmplitudeProvider'
import { useQRCodeDrawer } from '@/contexts/qrcodeDrawer'
import { getDeviceId } from '@/services/amplitude/getDeviceId'
import cn from '@/utils/cn'
import { getAllExperimentsAndVariants } from '@/utils/getAllExperimentsAndVariants'
import { getGaClientId } from '@/utils/getGaClientId'
import { getGASessionId } from '@/utils/getGASessionId'
import { handleAppUtmDynamicUrl } from '@/utils/handleAppUtmUrl'
import isMobileDevice from '@/utils/isMobileDevice'

import { button, ButtonContent, ButtonV2Props, link } from '../..'

export function ButtonOpenModalAccount(props: ButtonV2Props) {
  const {
    color,
    size,
    type = 'button',
    preFetch = false,
    className = '',
    linkPathname,
    linkQuery,
    linkHash,
    linkHref,
    linkTarget,
    isLoading,
    disabled,
    handleAppDownload = 'default',
    uiStyleVariant = 'button',
    trackOnClick,
    experiment
  } = props

  const router = useRouter()
  const [isDevice, setIsDevice] = useState(false)
  const [queryParamsFromCms, setQueryParamsFromCms] = useState<
    Record<string, string>
  >({})
  const { analytics } = useAmplitude()

  const { toggleDrawer, setQRCodelink, qrCodeModelValue } = useQRCodeDrawer()

  const [utmHref, setUtmHref] = useState<string | undefined>()

  useEffect(() => {
    const params = new URLSearchParams(linkQuery)
    const paramsObject = {} as Record<string, string>
    for (const [key, value] of params.entries()) {
      if (key !== 'null') {
        paramsObject[key] = value
      }
    }

    setQueryParamsFromCms(paramsObject)
  }, [linkQuery])

  useEffect(() => {
    setIsDevice(isMobileDevice())
  }, [])

  useEffect(() => {
    if (handleAppDownload && linkHref) {
      setQRCodelink(linkHref)

      const utmlinkUrl = handleAppUtmDynamicUrl(linkHref)

      setUtmHref(utmlinkUrl)
    }
  }, [handleAppDownload, linkHref, setQRCodelink])

  function handleLinkClick(
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) {
    const textSection = (event.nativeEvent as unknown as { section: string })
      .section

    analytics?.track({
      event_type: 'cta ton',
      event_properties: {
        name: 'cta ton',
        description: `Evento disparado ao clicar no CTA
             na seção "${textSection}"`,
        section_reference: textSection,
        cross_lead_id: getGASessionId(),
        client_id_ga: getGaClientId(),
        user_agent: window?.navigator?.userAgent,
        category: router.pathname,
        cta_reference: props.label,
        experiments: JSON.stringify(getAllExperimentsAndVariants())
      },
      device_id: getDeviceId(),
      user_agent: window?.navigator?.userAgent
    })
  }

  return handleAppDownload === 'variant_open_account' ? (
    <CMSTrackOnClickProvider
      trackOnClick={trackOnClick}
      experiment={experiment}
    >
      <button
        type={type}
        disabled={disabled || isLoading}
        className={cn({
          [button({ color, size, className })]: uiStyleVariant === 'button',
          [link({ color, className })]: uiStyleVariant === 'link'
        })}
        onClick={() => {
          toggleDrawer()
          qrCodeModelValue(handleAppDownload)
        }}
      >
        <ButtonContent {...props} />
      </button>
    </CMSTrackOnClickProvider>
  ) : isDevice ||
    linkPathname ||
    linkHash ||
    linkQuery ||
    (!handleAppDownload && !isDevice && linkHref) ? (
    <CMSTrackOnClickProvider
      trackOnClick={trackOnClick}
      experiment={experiment}
    >
      <Link
        href={
          utmHref ||
          linkHref || {
            pathname: linkPathname,
            query: { ...router.query, ...queryParamsFromCms },
            hash: linkHash
          }
        }
        target={linkTarget}
        prefetch={preFetch}
        className={cn(
          {
            [button({ color, size })]: uiStyleVariant === 'button',
            [link({ color })]: uiStyleVariant === 'link'
          },
          className
        )}
        onClick={(e) => {
          handleLinkClick(e)
        }}
      >
        <ButtonContent {...props} />
      </Link>
    </CMSTrackOnClickProvider>
  ) : (
    <CMSTrackOnClickProvider
      trackOnClick={trackOnClick}
      experiment={experiment}
    >
      <button
        type={type}
        disabled={disabled || isLoading}
        className={cn({
          [button({ color, size, className })]: uiStyleVariant === 'button',
          [link({ color, className })]: uiStyleVariant === 'link'
        })}
        onClick={(e) => {
          toggleDrawer()
          qrCodeModelValue(handleAppDownload)
          handleLinkClick(e)
        }}
      >
        <ButtonContent {...props} />
      </button>
    </CMSTrackOnClickProvider>
  )
}

export default ButtonOpenModalAccount
